import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { Modal, Row, Col, Skeleton, Button, notification, Dropdown } from 'antd';
import {
  PrinterFilled,
  SignatureFilled,
  ReloadOutlined,
  HistoryOutlined,
  EllipsisOutlined,
  BellOutlined
} from '@ant-design/icons';
import { useMediaQuery } from 'react-responsive';

import PaymentTab from './payment-tab';
import RoomInfoTab from './room-info-tab';
import AttachmentTab from './attachment-tab';
import GroupTab from './group-tab';

import useBookingStore from 'stores/useBooking';
import useModal from 'stores/useModal';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';

import {
  BATCH_ACTION_TYPE,
  BookingStatus,
  GroupBookingLineItem,
  ParamsBatchAction
} from 'services/api/type/booking.type';
import { queryClient } from 'index';
import { ACTION_BOOKING_LINE_DETAIL_MODAL } from 'constants/common';
import QUERY_KEYS from 'services/api/queryKeys';
// import API from 'services/api';
import {
  batchActionBookings,
  setMasterRoom,
  unsetMasterRoom
} from 'services/api/module/booking.api';
import { ReactComponent as IconEmail } from 'assets/images/email.svg';
import { ReactComponent as IconBedUser } from 'assets/images/bed-user.svg';
// import { ReactComponent as IconDollar } from 'assets/images/dollar.svg';
import { ReactComponent as Logout } from 'assets/images/logout.svg';
import { handleDisableCheckInButton } from 'utils';
import { CustomMenuItem, PermissionActionTypes } from 'helper/type/action.type';
import { usePermissions } from 'AuthorizePermission';
import { useFilterActions } from 'helper/common/permission.action.helper';
import { ACTION_BOOKING } from 'components/booking-list/action-booking';
import IBButton from 'helper/permission.action';
import { useTranslation } from 'react-i18next';

function BookingLineDetailModal() {
  const { t } = useTranslation();
  const {
    setInfoConfirmModal,
    setConfirmLoading,
    isOpenBookingDetail,
    bookingLineId,
    setOpenBookingDetail,
    setIsOpenViewPDF,
    setIsOpenConfirmationPDF,
    setIsOpenViewTrackingLogs,

    setIsOpenCancelRoom,
    // setIsOpenChangePrice,
    // setIsOpenChangePriceGroup,
    setIsOpenChangeDate,
    setIsOpenCancelAssignRoom,
    setIsOpenBookingConfirmation,
    setIsOpenBookingRemind
    // setIsOpenPayment
  } = useModal();

  const itemsMenuGroup: CustomMenuItem[] = useMemo(
    () => [
      {
        key: ACTION_BOOKING.CHANGE_DATE,
        label: t('bookingLineDetailModal.changeStayDate'),
        permission: PermissionActionTypes.CHANGE_DATE
      },
      {
        key: ACTION_BOOKING.ASSIGN_ROOM,
        label: t('bookingLineDetailModal.assignRoom'),
        permission: PermissionActionTypes.ASSIGN_ROOM
      },
      {
        key: ACTION_BOOKING.CANCEL_ASSIGN_ROOM,
        label: t('bookingLineDetailModal.unassignRoom'),
        permission: PermissionActionTypes.UNASSIGN_ROOM
      },
      {
        key: ACTION_BOOKING.SET_MASTER_ROOM,
        label: t('bookingLineDetailModal.setMasterRoom'),
        permission: PermissionActionTypes.SET_MASTER_ROOM
      },
      {
        key: ACTION_BOOKING.UNSET_MASTER_ROOM,
        label: t('bookingLineDetailModal.unsetMasterRoom'),
        permission: PermissionActionTypes.UNSET_MASTER_ROOM
      },
      {
        key: ACTION_BOOKING.CANCEL_BOOKING,
        label: t('bookingLineDetailModal.cancelOrNoShow')
      }
    ],
    [t]
  );

  const itemsMenu = useMemo(
    () => [
      {
        key: ACTION_BOOKING_LINE_DETAIL_MODAL.HISTORY,
        label: t('bookingLineDetailModal.history')
      },
      {
        key: ACTION_BOOKING_LINE_DETAIL_MODAL.SEND_EMAIL,
        label: t('bookingLineDetailModal.sendEmail')
      },
      {
        key: ACTION_BOOKING_LINE_DETAIL_MODAL.VIEW_PDF,
        label: t('bookingLineDetailModal.printInvoice')
      },
      {
        key: ACTION_BOOKING_LINE_DETAIL_MODAL.VIEW_CONFIRMATION,
        label: t('bookingLineDetailModal.printContract')
      },
      {
        key: ACTION_BOOKING_LINE_DETAIL_MODAL.RELOAD_BOOKING,
        label: t('bookingLineDetailModal.reload')
      }
    ],
    [t]
  );

  const { data: bookingLine, isFetching } = useGetBookingDetail(bookingLineId);

  const {
    isLoadingBookingDetail,
    checkedListBookingLineId,
    setCheckListBookingLineId,
    setIsActionOnGroup
  } = useBookingStore();

  const isIpadMobile = useMediaQuery({
    query: '(max-width: 991px)'
  });
  const isMobile = useMediaQuery({
    query: '(max-width: 768px)'
  });
  const isIpad = useMediaQuery({
    query: '(min-width: 769px) and (max-width: 1024px)'
  });

  const [tab, setTab] = useState(1);
  const [groupBookingLine, setGroupBookingLine] = useState<GroupBookingLineItem[]>([]);

  // const { mutateAsync: mutateSendEmail } = useMutation({
  //   mutationFn: (bookingId: number) => API.booking.sendEmail(bookingId)
  // });

  const { mutateAsync: mutateBatchActionBooking } = useMutation({
    mutationFn: (params: ParamsBatchAction) => batchActionBookings(params)
  });

  const { mutateAsync: mutateSetMasterRoom } = useMutation({
    mutationFn: (bookingLineId: number) => setMasterRoom(bookingLineId)
  });

  const { mutateAsync: mutateUnsetMasterRoom } = useMutation({
    mutationFn: (bookingLineId: number) => unsetMasterRoom(bookingLineId)
  });

  useEffect(() => {
    if (!isOpenBookingDetail) {
      setTab(1);
      setGroupBookingLine([]);
      setCheckListBookingLineId([]);
    }
  }, [isOpenBookingDetail, setCheckListBookingLineId]);

  useEffect(() => {
    if (bookingLine) {
      setGroupBookingLine(bookingLine?.group?.booking_lines || []);
    }
  }, [bookingLine]);

  const { permissions, hasPermission } = usePermissions();

  const menuPermission = useFilterActions(itemsMenuGroup, permissions, hasPermission);

  const handleClickMenu = (menu: any) => {
    switch (menu.key) {
      case ACTION_BOOKING_LINE_DETAIL_MODAL.HISTORY:
        handleViewTrackingLogs();
        break;
      case ACTION_BOOKING_LINE_DETAIL_MODAL.SEND_EMAIL:
        handleConfirmSendEmail();
        break;
      case ACTION_BOOKING_LINE_DETAIL_MODAL.VIEW_PDF:
        handleViewPdf();
        break;
      case ACTION_BOOKING_LINE_DETAIL_MODAL.VIEW_CONFIRMATION:
        handleViewConfirmation();
        break;
      case ACTION_BOOKING_LINE_DETAIL_MODAL.RELOAD_BOOKING:
        handleReloadBookingLine();
        break;
      default:
        break;
    }
  };

  // Action group
  const handleClickMenuGroup = (menu: any) => {
    switch (menu.key) {
      case ACTION_BOOKING.CANCEL_BOOKING:
        setIsActionOnGroup(true);
        setIsOpenCancelRoom(true);
        break;
      // case ACTION_BOOKING.CHANGE_PRICE: {
      //   if (checkedListBookingLineId.length === 1) {
      //     setIsOpenChangePrice(true);
      //   } else {
      //     setIsOpenChangePriceGroup(true);
      //   }
      //   break;
      // }
      case ACTION_BOOKING.ASSIGN_ROOM: {
        setInfoConfirmModal(true, {
          title: t('bookingLineDetailModal.confirmAutoAssign'),
          onOk: () => {
            setInfoConfirmModal(false);
            handleAutoAssign();
          }
        });
        break;
      }
      case ACTION_BOOKING.CHANGE_DATE: {
        setIsActionOnGroup(true);
        setIsOpenChangeDate(true);
        break;
      }
      case ACTION_BOOKING.CANCEL_ASSIGN_ROOM: {
        setIsActionOnGroup(true);
        setIsOpenCancelAssignRoom(true);
        break;
      }

      case ACTION_BOOKING.SET_MASTER_ROOM: {
        setIsActionOnGroup(true);
        handleSetMasterRoom();
        break;
      }

      case ACTION_BOOKING.UNSET_MASTER_ROOM: {
        setIsActionOnGroup(true);
        handleUnsetMasterRoom();
        break;
      }

      default:
        break;
    }
  };

  const handleCheckInGroup = () => {
    setInfoConfirmModal(true, {
      title: t('common.confirm.checkin'),
      onOk: async () => {
        setInfoConfirmModal(false);
        setConfirmLoading(true);
        try {
          await mutateBatchActionBooking({
            action: BATCH_ACTION_TYPE.CHECK_IN,
            booking_line_ids: checkedListBookingLineId
          });
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_TRACKING_LOGS, checkedListBookingLineId]
          });
          notification.success({
            message: t('common.message.checkIn')
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.KEY_ROOM]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
          });
          queryClient.invalidateQueries({
            queryKey: [
              QUERY_KEYS.KEY_ROOM,
              QUERY_KEYS.GET_BOOKING_DETAIL,
              bookingLine?.booking_line_id
            ]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
          });
        } catch (err: any) {
          notification.error({
            message: err.error || t('common.error')
          });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  const handleCheckOutGroup = () => {
    setInfoConfirmModal(true, {
      title: t('common.message.checkOut'),
      onOk: async () => {
        setInfoConfirmModal(false);
        setConfirmLoading(true);
        try {
          await mutateBatchActionBooking({
            action: BATCH_ACTION_TYPE.CHECK_OUT,
            booking_line_ids: checkedListBookingLineId
          });
          notification.success({
            message: t('common.message.checkout')
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.KEY_ROOM]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
          });
          queryClient.invalidateQueries({
            queryKey: [
              QUERY_KEYS.KEY_ROOM,
              QUERY_KEYS.GET_BOOKING_DETAIL,
              bookingLine?.booking_line_id
            ]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
          });
        } catch (err: any) {
          notification.error({
            message: err.error || t('common.error')
          });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  const handleAutoAssign = async () => {
    try {
      setConfirmLoading(true);

      const selectedBookingLines = bookingLine?.group?.booking_lines.filter(item =>
        checkedListBookingLineId.includes(item.id)
      );

      const unAssignedBookingLineId =
        selectedBookingLines?.filter(item => !item.room_id).map(item => item.id) || [];

      await mutateBatchActionBooking({
        action: BATCH_ACTION_TYPE.AUTO_ASSIGN,
        booking_line_ids: unAssignedBookingLineId
      });

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.KEY_ROOM]
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST]
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLine?.booking_line_id]
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
      });

      setIsOpenChangeDate(false);
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSetMasterRoom = async () => {
    setInfoConfirmModal(true, {
      title: t('common.actions.setMasterRoom'),
      message: t('common.confirm.setMasterRoom'),
      onOk: async () => {
        try {
          setInfoConfirmModal(false);
          setConfirmLoading(true);
          await mutateSetMasterRoom(checkedListBookingLineId[0]);
          notification.success({
            message: t('common.message.setMasterRoom')
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.KEY_ROOM]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
          });
          queryClient.invalidateQueries({
            queryKey: [
              QUERY_KEYS.KEY_ROOM,
              QUERY_KEYS.GET_BOOKING_DETAIL,
              bookingLine?.booking_line_id
            ]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_TRACKING_LOGS]
          });
        } catch (err: any) {
          notification.error({
            message: err.error || t('common.error')
          });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  const handleUnsetMasterRoom = async () => {
    setInfoConfirmModal(true, {
      title: t('common.actions.unsetMasterRoom'),
      onOk: async () => {
        try {
          setInfoConfirmModal(false);
          setConfirmLoading(true);
          await mutateUnsetMasterRoom(checkedListBookingLineId[0]);
          notification.success({
            message: t('common.message.unsetMasterRoom')
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.KEY_ROOM]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
          });
          queryClient.invalidateQueries({
            queryKey: [
              QUERY_KEYS.KEY_ROOM,
              QUERY_KEYS.GET_BOOKING_DETAIL,
              bookingLine?.booking_line_id
            ]
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_TRACKING_LOGS]
          });
        } catch (err: any) {
          notification.error({
            message: err.error || t('common.error')
          });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  // Action group

  const handleConfirmSendEmail = () => {
    // setInfoConfirmModal(true, {
    //   title: 'Xác nhận gửi mail',
    //   okText: 'Gửi',
    //   onOk: async () => {
    //     if (bookingLine?.booking_id) {
    //       try {
    //         setConfirmLoading(true);
    //         await mutateSendEmail(bookingLine.booking_id);
    //         setInfoConfirmModal(false);
    //         notification.success({
    //           message: 'Gửi mail thành công'
    //         });
    //       } catch (err: any) {
    //         notification.error({
    //           message: 'Gửi mail thất bại'
    //         });
    //       } finally {
    //         setConfirmLoading(false);
    //       }
    //     }
    //   }
    // });
    setIsOpenBookingConfirmation(true, bookingLine?.booking_id);
  };

  const handleViewPdf = () => {
    setIsOpenViewPDF(true);
  };

  const handleViewConfirmation = () => {
    setIsOpenConfirmationPDF(true);
  };

  const handleReloadBookingLine = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, bookingLine?.booking_line_id]
    });
  };
  const handleViewTrackingLogs = () => {
    setIsOpenViewTrackingLogs(true);
  };

  const handleViewBookingRemind = () => {
    setIsOpenBookingRemind(true);
  };

  // const handlePayment = () => {
  //   setIsOpenPayment(true);
  // };

  const menuActionGroup = useMemo(() => {
    const isHaveAssignedRoom = bookingLine?.group?.booking_lines
      .filter(item => checkedListBookingLineId.includes(item.id))
      .every(item => !!item.room_id && item.status === BookingStatus.CONFIRM);

    const isUnAssignedRoom = bookingLine?.group?.booking_lines
      .filter(item => checkedListBookingLineId.includes(item.id))
      .every(item => !item.room_id && item.status === BookingStatus.CONFIRM);

    const isNotCancelRoom = bookingLine?.group?.booking_lines
      .filter(item => checkedListBookingLineId.includes(item.id))
      .every(item => item.status === BookingStatus.CONFIRM);

    const isAllowChangeDate = bookingLine?.group?.booking_lines
      .filter(item => checkedListBookingLineId.includes(item.id))
      .every(
        item => item.status !== BookingStatus.CHECK_OUT && item.status !== BookingStatus.CANCEL
      );

    return menuPermission.filter(item => {
      if (!isHaveAssignedRoom && item.key === ACTION_BOOKING.CANCEL_ASSIGN_ROOM) {
        return false;
      }

      if (!isUnAssignedRoom && item.key === ACTION_BOOKING.ASSIGN_ROOM) {
        return false;
      }

      if (!isNotCancelRoom && item.key === ACTION_BOOKING.CANCEL_BOOKING) {
        return false;
      }

      if (!isAllowChangeDate && item.key === ACTION_BOOKING.CHANGE_DATE) {
        return false;
      }

      if (checkedListBookingLineId.length > 1 && item.key === ACTION_BOOKING.SET_MASTER_ROOM) {
        return false;
      }

      if (checkedListBookingLineId.length > 1 && item.key === ACTION_BOOKING.UNSET_MASTER_ROOM) {
        return false;
      }

      return true;
    });
  }, [bookingLine?.group?.booking_lines, checkedListBookingLineId, menuPermission]);

  const renderContent = () => {
    if (isLoadingBookingDetail) {
      return (
        <Row className="modal-content">
          <Col span={24} md={12}>
            <Skeleton />
          </Col>

          <Col span={24} md={12}>
            <Skeleton />
          </Col>
        </Row>
      );
    }

    if (bookingLine && tab === 1) {
      return <RoomInfoTab bookingLine={bookingLine} t={t} />;
    } else if (bookingLine && tab === 2) {
      return <PaymentTab bookingLine={bookingLine} t={t} />;
    } else if (bookingLine && tab === 3) {
      return <AttachmentTab bookingLine={bookingLine} />;
    }
  };

  const isShowGroupTab = !_.isEmpty(bookingLine?.group) && !isIpadMobile;

  // ====================
  const isAllowableCheckIn = useMemo(() => {
    const selectedBookingLines = bookingLine?.group?.booking_lines.filter(item =>
      checkedListBookingLineId.includes(item.id)
    );
    return selectedBookingLines?.every(item => item.status === BookingStatus.CONFIRM);
  }, [bookingLine?.group?.booking_lines, checkedListBookingLineId]);

  // ====================
  const isAllowableCheckOut = useMemo(() => {
    const selectedBookingLines = bookingLine?.group?.booking_lines.filter(item =>
      checkedListBookingLineId.includes(item.id)
    );
    return selectedBookingLines?.every(item => item.status === BookingStatus.CHECK_IN);
  }, [bookingLine?.group?.booking_lines, checkedListBookingLineId]);

  // ====================
  const isAllowableShowAction = useMemo(() => {
    const selectedBookingLines = bookingLine?.group?.booking_lines.filter(item =>
      checkedListBookingLineId.includes(item.id)
    );
    return selectedBookingLines?.some(
      item => item.status !== BookingStatus.CANCEL && item.status !== BookingStatus.CHECK_OUT
    );
  }, [bookingLine?.group?.booking_lines, checkedListBookingLineId]);

  return (
    <Modal
      title={null}
      centered
      width={isShowGroupTab ? 1400 : 1100}
      open={isOpenBookingDetail}
      onCancel={() => setOpenBookingDetail(false)}
      footer={null}
      closeIcon={isMobile || isIpad ? true : null}
      className={classNames('modal-booking-line', {
        'modal-booking-line--info': tab === 1,
        'modal-booking-line--payment': tab === 2
      })}
    >
      {isShowGroupTab && (
        <GroupTab bookingLines={groupBookingLine} activeBookingLineId={bookingLineId} />
      )}

      <div className="flex-grow">
        <div className="ant-modal-header">
          <div className="ant-modal-title">
            <div className="modal-booking-line-tab-header">
              {!_.isEmpty(checkedListBookingLineId) && isAllowableShowAction ? (
                <div className="group-actions">
                  {isAllowableCheckIn && (
                    <IBButton
                      permission={PermissionActionTypes.CHECK_IN}
                      className="ant-btn-checkin"
                      icon={<IconBedUser />}
                      onClick={handleCheckInGroup}
                      disabled={handleDisableCheckInButton(bookingLine)}
                    >
                      {t('common.actions.checkIn')}
                    </IBButton>
                  )}

                  {isAllowableCheckOut && (
                    <IBButton
                      permission={PermissionActionTypes.CHECK_OUT}
                      className="ant-btn-checkout"
                      onClick={handleCheckOutGroup}
                      icon={<Logout />}
                    >
                      {t('common.actions.checkOut')}
                    </IBButton>
                  )}

                  {/* <Button className="ant-btn-pay" icon={<IconDollar />} onClick={handlePayment}>
                    Thanh toán
                  </Button> */}

                  {isAllowableShowAction && (
                    <Dropdown
                      menu={{ items: menuActionGroup, onClick: handleClickMenuGroup }}
                      trigger={['click']}
                      className="menu-action"
                    >
                      <Button className="btn-ellipsis">
                        <EllipsisOutlined />
                      </Button>
                    </Dropdown>
                  )}
                </div>
              ) : (
                <ul className="modal-booking-line-tabs">
                  <li className={tab === 1 ? 'active' : ''} onClick={() => setTab(1)}>
                    {t('common.bookingData.room')}
                  </li>
                  <li className={tab === 2 ? 'active' : ''} onClick={() => setTab(2)}>
                    {t('common.actions.payment')}
                  </li>
                  <li className={tab === 3 ? 'active' : ''} onClick={() => setTab(3)}>
                    {t('common.actions.attachment')}
                  </li>
                </ul>
              )}

              <div className="modal-booking-line-action">
                {isMobile && (
                  <Dropdown
                    menu={{ items: itemsMenu, onClick: handleClickMenu }}
                    trigger={['click']}
                    className="menu-action"
                  >
                    <Button className="btn-ellipsis">
                      <EllipsisOutlined />
                    </Button>
                  </Dropdown>
                )}

                {!isMobile && (
                  <>
                    <IBButton
                      permission={PermissionActionTypes.BOOKING_REMIND}
                      type="link"
                      onClick={handleViewBookingRemind}
                      style={{ padding: 0, height: '24px' }}
                    >
                      <BellOutlined />
                    </IBButton>
                    <IBButton
                      permission={PermissionActionTypes.ROOM_LOG}
                      type="link"
                      onClick={handleViewTrackingLogs}
                      style={{ padding: 0, height: '24px' }}
                    >
                      <HistoryOutlined />
                    </IBButton>
                    <Button
                      type="link"
                      onClick={handleConfirmSendEmail}
                      style={{ padding: 0, height: '24px' }}
                    >
                      <IconEmail />
                    </Button>
                    <Button
                      type="link"
                      onClick={handleViewPdf}
                      style={{ padding: 0, height: '24px' }}
                    >
                      <PrinterFilled color="#000" />
                    </Button>
                    <Button
                      type="link"
                      onClick={handleViewConfirmation}
                      style={{ padding: 0, height: '24px' }}
                    >
                      <SignatureFilled color="#000" />
                    </Button>
                    <Button
                      type="link"
                      onClick={handleReloadBookingLine}
                      style={{ padding: 0, height: '24px' }}
                    >
                      <ReloadOutlined color="#000" spin={isFetching} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {renderContent()}
      </div>
    </Modal>
  );
}

export default BookingLineDetailModal;
